import React, { useState, useEffect } from 'react';
import { ArrowLeft, User, CreditCard, Clock, Edit2, Save, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useModal } from './components/CustomModal';
import axios from 'axios';
axios.defaults.withCredentials = true;
import API from './apiConfig';

export default function ProfilePage({ onBack }) {
  const { user, refreshUser, logout } = useAuth();
  const modal = useModal();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', company: '' });
  const [saving, setSaving] = useState(false);
  const [subInfo, setSubInfo] = useState(null);
  const [message, setMessage] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    if (user) setForm({ first_name: user.first_name || '', last_name: user.last_name || '', company: user.company || '' });
    axios.get(`${API}/api/stripe/subscription-info`).then(r => setSubInfo(r.data)).catch(() => {});
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/user/profile`, form);
      await refreshUser();
      setEditing(false);
      setMessage('Profil gespeichert');
      setTimeout(() => setMessage(''), 2000);
    } catch (e) { setMessage(e.response?.data?.detail || 'Fehler'); }
    setSaving(false);
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/stripe/create-checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setMessage(e.response?.data?.detail || 'Fehler'); setCheckoutLoading(false); }
  };

  const handleCancel = async () => {
    if (!await modal.confirm('Abonnement wirklich kündigen? Sie verlieren den Zugang nach Ende des Abrechnungszeitraums.', { title: 'Abo kündigen', confirmText: 'Kündigen', type: 'warning' })) return;
    setCancelLoading(true);
    try {
      await axios.post(`${API}/api/stripe/cancel-subscription`);
      await refreshUser();
      setSubInfo(prev => ({ ...prev, subscription_status: 'cancelled' }));
      setMessage('Abonnement gekündigt');
    } catch (e) { setMessage(e.response?.data?.detail || 'Fehler'); }
    setCancelLoading(false);
  };

  if (!user) return null;

  const isActive = subInfo?.subscription_status === 'active' || user.role === 'admin';
  const hasTrial = subInfo?.trial_end && new Date(subInfo.trial_end) > new Date();
  const trialDaysLeft = hasTrial ? Math.ceil((new Date(subInfo.trial_end) - new Date()) / 86400000) : 0;
  const analysesCount = subInfo?.analyses_count || user.analyses_count || 0;
  const canAnalyze = isActive || hasTrial || analysesCount === 0;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="border-b border-zinc-800 bg-zinc-900/80">
        <div className="max-w-2xl mx-auto px-6 py-3 flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 hover:bg-zinc-800 rounded transition-colors"><ArrowLeft className="w-4 h-4 text-zinc-400" /></button>
          <User className="w-5 h-5 text-blue-400" />
          <span className="font-mono text-sm font-bold">Mein Profil</span>
        </div>
      </div>

      {message && (
        <div className="max-w-2xl mx-auto px-6 mt-4">
          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-400 text-sm font-mono">{message}</div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-6 py-6 space-y-6">
        {/* Profile Card */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-mono text-sm text-zinc-400 uppercase tracking-wider">Persönliche Daten</h2>
            {!editing ? (
              <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-xs font-mono"><Edit2 className="w-3 h-3" /> Bearbeiten</button>
            ) : (
              <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 text-green-400 hover:text-green-300 text-xs font-mono">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Speichern
              </button>
            )}
          </div>
          {editing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-[10px] text-zinc-500 font-mono mb-1">Vorname</label>
                  <input value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm font-mono text-white focus:border-blue-500 focus:outline-none" /></div>
                <div><label className="block text-[10px] text-zinc-500 font-mono mb-1">Nachname</label>
                  <input value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm font-mono text-white focus:border-blue-500 focus:outline-none" /></div>
              </div>
              <div><label className="block text-[10px] text-zinc-500 font-mono mb-1">Firma</label>
                <input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm font-mono text-white focus:border-blue-500 focus:outline-none" /></div>
            </div>
          ) : (
            <div className="space-y-2 font-mono text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">Name</span><span className="text-white">{user.first_name} {user.last_name}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">E-Mail</span><span className="text-white">{user.email}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Firma</span><span className="text-white">{user.company}</span></div>
              {user.referral_code && <div className="flex justify-between"><span className="text-zinc-500">Referenzcode</span><span className="text-zinc-300">{user.referral_code}</span></div>}
              <div className="flex justify-between"><span className="text-zinc-500">E-Mail bestätigt</span>
                <span className={user.email_verified ? 'text-green-400' : 'text-amber-400'}>{user.email_verified ? 'Ja' : 'Nein'}</span></div>
            </div>
          )}
        </div>

        {/* Subscription Card */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
          <h2 className="font-mono text-sm text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Abonnement</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-zinc-500">Status</span>
              <span className={`font-mono text-sm font-bold ${isActive ? 'text-green-400' : 'text-zinc-400'}`}>
                {user.role === 'admin' ? 'Admin (Vollzugang)' : isActive ? 'Aktiv' : subInfo?.subscription_status === 'cancelled' ? 'Gekündigt' : 'Inaktiv'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-zinc-500">Analysen durchgeführt</span>
              <span className="font-mono text-sm text-white">{analysesCount}</span>
            </div>
            {subInfo?.plan_price && user.role !== 'admin' && (
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-zinc-500">Plan</span>
                <span className="font-mono text-sm text-white">LAIzyBlueprint Pro – {subInfo.plan_price}€/Monat</span>
              </div>
            )}

            {/* Trial Info */}
            {hasTrial && (
              <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center gap-3">
                <Clock className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <div>
                  <p className="font-mono text-sm text-blue-400 font-bold">Testzeitraum aktiv</p>
                  <p className="font-mono text-xs text-blue-300">{trialDaysLeft} {trialDaysLeft === 1 ? 'Tag' : 'Tage'} verbleibend (bis {new Date(subInfo.trial_end).toLocaleDateString('de-DE')})</p>
                </div>
              </div>
            )}

            {/* Access Status */}
            {!canAnalyze && user.role !== 'admin' && (
              <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <div>
                  <p className="font-mono text-sm text-amber-400">Kein aktiver Zugang</p>
                  <p className="font-mono text-xs text-amber-300">Abonnieren Sie LAIzyBlueprint Pro um weitere Analysen durchzuführen.</p>
                </div>
              </div>
            )}

            {canAnalyze && !isActive && !hasTrial && analysesCount === 0 && user.role !== 'admin' && (
              <div className="mt-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <div>
                  <p className="font-mono text-sm text-green-400">Erste Analyse kostenlos</p>
                  <p className="font-mono text-xs text-green-300">Sie können Ihre erste Grundrissanalyse kostenlos durchführen.</p>
                </div>
              </div>
            )}

            {/* Actions */}
            {user.role !== 'admin' && (
              <div className="mt-4 pt-4 border-t border-zinc-800 space-y-2">
                {!isActive && (
                  <button data-testid="subscribe-btn" onClick={handleCheckout} disabled={checkoutLoading}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white font-mono text-sm rounded-lg transition-colors flex items-center justify-center gap-2">
                    {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                    LAIzyBlueprint Pro abonnieren – {subInfo?.plan_price || 269}€/Monat
                  </button>
                )}
                {isActive && subInfo?.stripe_subscription_id && (
                  <button data-testid="cancel-sub-btn" onClick={handleCancel} disabled={cancelLoading}
                    className="w-full py-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 font-mono text-xs rounded-lg transition-colors">
                    {cancelLoading ? 'Kündigen...' : 'Abonnement kündigen'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Logout */}
        <button onClick={logout} className="w-full py-2 border border-zinc-700 text-zinc-400 hover:text-red-400 hover:border-red-500/30 font-mono text-sm rounded-lg transition-colors">
          Abmelden
        </button>
      </div>
    </div>
  );
}
